import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Form, Row } from 'antd';
import type { Rule } from 'antd/es/form';
import { FormInput } from 'components/Custom/inputs';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ContactType } from 'store/app/enums';
import { turnDrawerOff } from 'store/app/actions';
import {
  createExcelContactRequest,
  updateExcelContactRequest,
} from 'store/organisation/actions';

import { getCommonCountries } from 'store/common/selectors';
import { Contact } from 'store/vat-checker/models';
import { getContactTypeOptions } from 'utils/display-enum-label';
import { validateEmail } from 'utils/validation/rules';
import { TransactionEditDrawerLinesScroll } from './transactions-tab/styles';

const optionalEmailRule = (message: string): Rule => ({
  validator: (_, value) => {
    const trimmed = (value ?? '').trim();
    if (!trimmed || validateEmail(trimmed)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(message));
  },
});

const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 12,
  color: '#5a6b6b',
  margin: '18px 0 10px',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

const trimOrUndefined = (value?: string) => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const resolveContactTypeValue = (contactType?: string | null) => {
  if (!contactType?.trim()) return undefined;
  const trimmed = contactType.trim();
  if (ContactType[trimmed as keyof typeof ContactType]) return trimmed;
  const entry = Object.entries(ContactType).find(
    ([, label]) => label === trimmed
  );
  return entry?.[0];
};

type FieldType = {
  name: string;
  email?: string;
  phone?: string;
  taxNumber?: string;
  contactType?: string;
  country?: string;
  address?: string;
  address1?: string;
  zipCode?: string;
  mainContact?: string;
  mainContactEmail?: string;
  otherContact?: string;
  otherContactEmail?: string;
};

const contactToFormState = (contact?: Contact | null): FieldType => ({
  name: contact?.name ?? '',
  email: contact?.email ?? '',
  phone: contact?.phone ?? '',
  taxNumber: contact?.taxNumber ?? '',
  contactType: resolveContactTypeValue(contact?.contactType),
  country: trimOrUndefined(contact?.country),
  address: contact?.address ?? '',
  address1: contact?.address1 ?? '',
  zipCode: contact?.zipCode ?? '',
  mainContact: contact?.mainContact ?? '',
  mainContactEmail: contact?.mainContactEmail ?? '',
  otherContact: contact?.otherContact ?? '',
  otherContactEmail: contact?.otherContactEmail ?? '',
});

interface AddCustomContactProps {
  organisationId: string;
  contact?: Contact | null;
}

export const AddCustomContact: React.FC<AddCustomContactProps> = ({
  organisationId,
  contact,
}) => {
  const { t } = useTranslation();
  const contactTypeOptions = React.useMemo(
    () => getContactTypeOptions(t),
    [t]
  );
  const dispatch = useDispatch();
  const [form] = Form.useForm<FieldType>();
  const countriesRaw = useSelector(getCommonCountries);
  const countries = Array.isArray(countriesRaw) ? countriesRaw : [];
  const contactId = contact?.contactUuid || contact?.providerContactId;
  const isEditMode = !!contactId;

  React.useEffect(() => {
    form.setFieldsValue(contactToFormState(contact));
  }, [contact, form]);

  const countryOptions = React.useMemo(
    () =>
      [...countries]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ label: c.name, value: c.name })),
    [countries]
  );

  const handleSubmit = (values: FieldType) => {
    if (!organisationId) return;

    const payload = {
      name: values.name.trim(),
      email: trimOrUndefined(values.email),
      phone: trimOrUndefined(values.phone),
      taxNumber: trimOrUndefined(values.taxNumber),
      contactType: trimOrUndefined(values.contactType),
      country: trimOrUndefined(values.country),
      address: trimOrUndefined(values.address),
      address1: trimOrUndefined(values.address1),
      zipCode: trimOrUndefined(values.zipCode),
      mainContact: trimOrUndefined(values.mainContact),
      mainContactEmail: trimOrUndefined(values.mainContactEmail),
      otherContact: trimOrUndefined(values.otherContact),
      otherContactEmail: trimOrUndefined(values.otherContactEmail),
    } as Contact;

    if (isEditMode && contactId) {
      dispatch(updateExcelContactRequest(organisationId, contactId, payload));
    } else {
      dispatch(createExcelContactRequest(organisationId, payload));
    }

    dispatch(turnDrawerOff());
  };

  const renderTextField = (
    name: keyof FieldType,
    label: string,
    placeholder?: string,
    rules?: Rule[]
  ) => (
    <>
      <LabelWrapper>
        <span className="label-text">{label}</span>
      </LabelWrapper>
      <Form.Item<FieldType> name={name} rules={rules}>
        <FormInput
          width="100%"
          placeholder={placeholder}
          //@ts-ignore
          border_color="var(--color-border, #dbeae3)"
        />
      </Form.Item>
    </>
  );

  return (
    <TransactionEditDrawerLinesScroll>
      <Row justify="center">
        <Col span={24}>
          <Form
            form={form}
            name="basic"
            autoComplete="off"
            onFinish={handleSubmit}
            initialValues={contactToFormState(contact)}
          >
            <div style={sectionTitleStyle}>{t('organisation.basic-details')}</div>

            {renderTextField(
              'name',
              t('common.name'),
              t('contacts.enter-contact-name'),
              [{ required: true, message: t('common.required-field') }]
            )}

            <LabelWrapper>
              <span className="label-text">
                {t('contacts.contactType')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType> name="contactType">
              <FormSelect
                allowClear
                placeholder={t('common.select')}
                options={contactTypeOptions}
                width="100%"
                height="45px"
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
              />
            </Form.Item>

            {renderTextField(
              'email',
              t('signup.email-address'),
              t('contacts.enter-email-address'),
              [optionalEmailRule(t('signup.invalid-email'))]
            )}

            {renderTextField(
              'phone',
              t('contacts.phone'),
              t('contacts.enter-phone-number')
            )}

            {renderTextField(
              'taxNumber',
              t('main.tax-number'),
              t('contacts.enter-tax-number')
            )}

            <div style={sectionTitleStyle}>
              {t('contacts.additional-contacts')}
            </div>

            {renderTextField(
              'mainContact',
              t('vat-checker.primary-contact'),
              t('contacts.enter-primary-contact-name')
            )}

            {renderTextField(
              'mainContactEmail',
              t('vat-checker.primary-contact-email'),
              t('contacts.enter-primary-contact-email'),
              [optionalEmailRule(t('signup.invalid-email'))]
            )}

            {renderTextField(
              'otherContact',
              t('vat-checker.secondary-contact'),
              t('contacts.enter-secondary-contact-name')
            )}

            {renderTextField(
              'otherContactEmail',
              t('vat-checker.secondary-contact-email'),
              t('contacts.enter-secondary-contact-email'),
              [optionalEmailRule(t('signup.invalid-email'))]
            )}

            <div style={sectionTitleStyle}>{t('vat-checker.address')}</div>

            <LabelWrapper>
              <span className="label-text">{t('common.country')}</span>
            </LabelWrapper>
            <Form.Item<FieldType> name="country">
              <FormSelect
                showSearch
                allowClear
                placeholder={t('common.select')}
                options={countryOptions}
                filterOption={(input: string, option: any) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                width="100%"
                height="45px"
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
              />
            </Form.Item>

            {renderTextField(
              'address',
              t('organisation.registered-address'),
              t('contacts.enter-registered-address')
            )}

            {renderTextField(
              'address1',
              t('signup.address-line1'),
              t('contacts.enter-address-line1')
            )}

            {renderTextField(
              'zipCode',
              t('signup.zip-code'),
              t('contacts.enter-zip-postal-code')
            )}

            <Form.Item>
              <ScopeButton type="primary" htmlType="submit" width="100%">
                {isEditMode
                  ? t('contacts.save-contact')
                  : t('organisation.add-contact')}
              </ScopeButton>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </TransactionEditDrawerLinesScroll>
  );
};

